import { BEAD_SPECS } from './beads';
import type { Cell, Pattern, StitchId } from './types';

export interface StitchSpec {
  id: StitchId;
  label: string;
  blurb: string;
  /** Peyote is the only stitch where odd-count changes the thread path. */
  supportsOddCount: boolean;
  /** Worked in the round as a tube, so the left and right edges join up. */
  isRope: boolean;
}

export const STITCH_SPECS: Record<StitchId, StitchSpec> = {
  square: {
    id: 'square',
    label: 'Square stitch',
    blurb: 'Straight grid, beads line up in rows and columns. Off-loom equivalent of loomwork.',
    supportsOddCount: false,
    isRope: false,
  },
  loom: {
    id: 'loom',
    label: 'Loom',
    blurb: 'Straight grid on a warped loom. Same chart as square stitch.',
    supportsOddCount: false,
    isRope: false,
  },
  peyote: {
    id: 'peyote',
    label: 'Peyote (gourd)',
    blurb: 'Beads interlock in vertical columns, every other column dropped half a bead.',
    supportsOddCount: true,
    isRope: false,
  },
  brick: {
    id: 'brick',
    label: 'Brick stitch',
    blurb: 'Beads sit like bricks — holes vertical, every other row shifted half a bead.',
    supportsOddCount: false,
    isRope: false,
  },
  herringbone: {
    id: 'herringbone',
    label: 'Herringbone (Ndebele)',
    blurb: 'Beads worked in pairs that tilt toward each other, forming a V-shaped weave.',
    supportsOddCount: false,
    isRope: false,
  },
  crochet: {
    id: 'crochet',
    label: 'Bead crochet rope',
    blurb: 'A tube: the pattern wraps around, each round spiralling one bead sideways.',
    supportsOddCount: false,
    isRope: true,
  },
};

export const STITCH_LIST = Object.values(STITCH_SPECS);

/**
 * Everything needed to place beads in space, in millimetres.
 *
 * Only one of the two stagger axes is ever non-zero: stitches either shift
 * alternate rows sideways (brick, crochet) or alternate columns downward
 * (peyote). That keeps the inverse mapping in `cellAt` unambiguous.
 */
export interface Layout {
  /** Horizontal pitch between bead centres, mm. */
  cellW: number;
  /** Vertical pitch between bead centres, mm. */
  cellH: number;
  /** Extra gap inserted after every second column (herringbone pairs), mm. */
  pairGap: number;
  /** Half-cell sideways shift applied to odd rows, mm. 0 if unused. */
  rowStagger: number;
  /** Half-cell downward shift applied to odd columns, mm. 0 if unused. */
  colStagger: number;
  totalW: number;
  totalH: number;
  /** Top-left corner of the bead box at this cell, mm. */
  pos(col: number, row: number): { x: number; y: number };
  /** Grid cell containing this point, or null if outside the pattern. */
  cellAt(x: number, y: number): Cell | null;
  /**
   * Same mapping, but keeps going past the edges and returns out-of-range
   * coordinates. Drawing uses this so a stroke that wanders off the pattern
   * and back again stays connected instead of jumping.
   */
  cellAtUnclamped(x: number, y: number): Cell;
}

export function layoutFor(pattern: Pattern): Layout {
  return buildLayout(pattern.stitch, pattern.beadSize, pattern.width, pattern.height);
}

export function buildLayout(
  stitch: StitchId,
  beadSize: Pattern['beadSize'],
  width: number,
  height: number,
): Layout {
  const spec = BEAD_SPECS[beadSize];

  // Holes run horizontally in loom/square/peyote (so the bead is narrow across
  // the row and tall between rows) and vertically in brick/herringbone/crochet.
  const holesHorizontal = stitch === 'square' || stitch === 'loom' || stitch === 'peyote';
  const cellW = holesHorizontal ? spec.len : spec.dia;
  const cellH = holesHorizontal ? spec.dia : spec.len;

  const pairGap = stitch === 'herringbone' ? spec.dia * 0.28 : 0;
  const rowStagger = stitch === 'brick' || stitch === 'crochet' ? cellW / 2 : 0;
  const colStagger = stitch === 'peyote' ? cellH / 2 : 0;

  const colX = (col: number) => col * cellW + Math.floor(col / 2) * pairGap;
  const totalW = width > 0 ? colX(width - 1) + cellW + rowStagger : 0;
  const totalH = height > 0 ? (height - 1) * cellH + cellH + colStagger : 0;

  const pos = (col: number, row: number) => ({
    x: colX(col) + (row % 2 === 1 ? rowStagger : 0),
    y: row * cellH + (col % 2 === 1 ? colStagger : 0),
  });

  const invColX = (x: number): number => {
    if (pairGap === 0) return Math.floor(x / cellW);
    const pairWidth = cellW * 2 + pairGap;
    const pair = Math.floor(x / pairWidth);
    const rem = x - pair * pairWidth;
    // A click landing in the gap between pairs snaps to the nearer bead.
    const within = rem < cellW ? 0 : 1;
    return pair * 2 + within;
  };

  const cellAtUnclamped = (x: number, y: number): Cell => {
    if (colStagger > 0) {
      const col = invColX(x);
      const row = Math.floor((y - (Math.abs(col % 2) === 1 ? colStagger : 0)) / cellH);
      return { col, row };
    }
    const row = Math.floor(y / cellH);
    const col = invColX(x - (Math.abs(row % 2) === 1 ? rowStagger : 0));
    return { col, row };
  };

  const cellAt = (x: number, y: number): Cell | null => {
    const { col, row } = cellAtUnclamped(x, y);
    if (col < 0 || col >= width || row < 0 || row >= height) return null;
    return { col, row };
  };

  return { cellW, cellH, pairGap, rowStagger, colStagger, totalW, totalH, pos, cellAt, cellAtUnclamped };
}

/**
 * Whether a flat peyote piece is odd- or even-count.
 *
 * This is not a preference: it follows from how many beads sit across the
 * piece. An odd number means every other row ends without a bead to stitch
 * into, which is what forces the odd-count turnaround.
 */
export function peyoteCount(pattern: Pattern): 'odd' | 'even' {
  return pattern.width % 2 === 1 ? 'odd' : 'even';
}

export interface WovenRow {
  /** 1-based row number as a beader would count it. */
  number: number;
  label: string;
  /** Cells in the order they are stitched. */
  cells: Cell[];
}

/**
 * The pattern re-expressed as the sequence of rows you actually work, from the
 * start of the piece to the end. This drives the word chart and the thread-path
 * overlay, so it is the one place that knows how each stitch is built.
 *
 * Beadwork grows upward, so row 1 is the bottom grid row.
 */
export function wovenRows(pattern: Pattern): WovenRow[] {
  const { width, height, stitch } = pattern;
  const rows: WovenRow[] = [];
  const rangeCols = (step: number, start: number) => {
    const out: number[] = [];
    for (let c = start; c < width; c += step) out.push(c);
    return out;
  };

  if (stitch === 'peyote') {
    // Within one grid row the odd (dropped) columns sit lower, so they are
    // stitched first when working from the bottom up.
    let n = 0;
    for (let r = height - 1; r >= 0; r--) {
      for (const parity of [1, 0]) {
        const cols = rangeCols(2, parity);
        if (cols.length === 0) continue;
        n += 1;
        // Beadwork is worked back and forth: odd rows right-to-left here.
        const ordered = n % 2 === 1 ? [...cols].reverse() : cols;
        rows.push({
          number: n,
          label: `Row ${n}`,
          cells: ordered.map((col) => ({ col, row: r })),
        });
      }
    }
    return rows;
  }

  if (stitch === 'crochet') {
    // A rope is one continuous spiral; each grid row is one round.
    let n = 0;
    for (let r = height - 1; r >= 0; r--) {
      n += 1;
      const cells: Cell[] = [];
      for (let c = 0; c < width; c++) cells.push({ col: c, row: r });
      rows.push({ number: n, label: `Round ${n}`, cells });
    }
    return rows;
  }

  let n = 0;
  for (let r = height - 1; r >= 0; r--) {
    n += 1;
    const cols: number[] = [];
    for (let c = 0; c < width; c++) cols.push(c);
    const ordered = n % 2 === 1 ? cols : [...cols].reverse();
    rows.push({ number: n, label: `Row ${n}`, cells: ordered.map((col) => ({ col, row: r })) });
  }
  return rows;
}

/** Physical finished size of the piece, in millimetres. */
export function physicalSize(pattern: Pattern): { w: number; h: number } {
  const l = layoutFor(pattern);
  return { w: l.totalW, h: l.totalH };
}
